import {
  EXTREME_FREE_SPIN_WILD_STOP_WEIGHT_UNITS,
  STOP_WEIGHT_BASE_UNITS,
} from '../core/reel-strips';
import { buildFreeSpinStopProfiles, type FreeSpinStopProfiles } from '../core/reels';
import { resolveSpin, type SpinResult } from '../core/spin-engine';
import type { Grid, GridPosition, MoneyCents, RandomSource, ReelColumn, ReelIndex, ReelStops, RowIndex } from '../core/types';
import type { WildMultiplierPosition } from '../core/wins';
import { applyStickyWilds, normalizeStickyWilds } from './sticky-wilds';
import type { EgyptianGodEvent, ObeliskCandidate, SliferTarget } from './types';

export const GOD_TRIGGER_CHANCES = Object.freeze({ RA: 0.10, SLIFER: 0.10, OBELISK: 0.10 } as const);
export const RA_WEIGHT_INCREMENT_UNITS = Math.round(STOP_WEIGHT_BASE_UNITS * 0.30);
export const EXTREME_OPENING_WEIGHT_INCREMENT_UNITS = Math.round(STOP_WEIGHT_BASE_UNITS * 0.15);
export const EXTREME_OPENING_SPINS = 10;

export interface GodTriggers { readonly ra: boolean; readonly slifer: boolean; readonly obelisk: boolean }

export function rollEgyptianGods(random: RandomSource): GodTriggers {
  return Object.freeze({
    ra: random.next() < GOD_TRIGGER_CHANCES.RA,
    slifer: random.next() < GOD_TRIGGER_CHANCES.SLIFER,
    obelisk: random.next() < GOD_TRIGGER_CHANCES.OBELISK,
  });
}

export function createExtremeFreeSpinProfiles(
  raBoostCount: number,
  completedFeatureSpins = EXTREME_OPENING_SPINS,
): FreeSpinStopProfiles {
  if (!Number.isSafeInteger(raBoostCount) || raBoostCount < 0) throw new RangeError('Ra-Stufe ist ungültig.');
  if (!Number.isSafeInteger(completedFeatureSpins) || completedFeatureSpins < 0) {
    throw new RangeError('Anzahl gespielter Feature-Spins ist ungültig.');
  }
  const openingIncrement = completedFeatureSpins < EXTREME_OPENING_SPINS
    ? EXTREME_OPENING_WEIGHT_INCREMENT_UNITS
    : 0;
  const increment = raBoostCount * RA_WEIGHT_INCREMENT_UNITS + openingIncrement;
  if (!Number.isSafeInteger(increment)) throw new RangeError('Ra-Verstärkung ist zu groß.');
  return buildFreeSpinStopProfiles(Object.freeze(EXTREME_FREE_SPIN_WILD_STOP_WEIGHT_UNITS.map(
    (weight) => weight + increment,
  )) as unknown as readonly [number, number, number, number, number]);
}

function samePosition(left: GridPosition, right: GridPosition): boolean {
  return left.reel === right.reel && left.row === right.row;
}

export function applySlifer(
  grid: Grid,
  sticky: readonly WildMultiplierPosition[],
  blocked: GridPosition | null,
  random: RandomSource,
): Readonly<{ sticky: readonly WildMultiplierPosition[]; event: EgyptianGodEvent }> {
  const current = [...normalizeStickyWilds(sticky)];
  const targets: SliferTarget[] = [];
  if (current.length < 2) {
    const occupied = new Set(current.map(positionKey));
    const eligible: GridPosition[] = [];
    grid.forEach((column, reel) => column.forEach((symbol, row) => {
      const position = { reel: reel as ReelIndex, row: row as RowIndex };
      if (
        symbol !== 'SCATTER'
        && !occupied.has(positionKey(position))
        && (blocked === null || !samePosition(position, blocked))
      ) eligible.push(position);
    }));
    const count = Math.min(2, eligible.length);
    for (let index = 0; index < count; index += 1) {
      const selected = eligible.splice(random.integer(eligible.length), 1)[0]!;
      current.push(Object.freeze({ ...selected, multiplier: 1 }));
      targets.push(Object.freeze({ ...selected, previousMultiplier: 0, multiplier: 1 }));
    }
  } else {
    const pool = [...current];
    const distinctCount = Math.min(3, pool.length);
    for (let index = 0; index < distinctCount; index += 1) {
      const selectedIndex = random.integer(pool.length);
      const selected = pool.splice(selectedIndex, 1)[0]!;
      const liveIndex = current.findIndex((entry) => samePosition(entry, selected));
      const previousMultiplier = current[liveIndex]!.multiplier ?? 1;
      const upgraded = Object.freeze({ ...selected, multiplier: previousMultiplier + 1 });
      if (!Number.isSafeInteger(upgraded.multiplier)) throw new RangeError('Slifer-Multiplikator ist zu groß.');
      current[liveIndex] = upgraded;
      targets.push(Object.freeze({ ...upgraded, previousMultiplier }));
    }
    // With exactly two sticky wilds, both are upgraded once before one of
    // them receives Slifer's third upgrade. The recorded transition keeps
    // presentation and replay deterministic even when a target repeats.
    if (current.length === 2) {
      const liveIndex = random.integer(current.length);
      const selected = current[liveIndex]!;
      const previousMultiplier = selected.multiplier ?? 1;
      const upgraded = Object.freeze({ ...selected, multiplier: previousMultiplier + 1 });
      if (!Number.isSafeInteger(upgraded.multiplier)) throw new RangeError('Slifer-Multiplikator ist zu groß.');
      current[liveIndex] = upgraded;
      targets.push(Object.freeze({ ...upgraded, previousMultiplier }));
    }
  }
  return Object.freeze({
    sticky: normalizeStickyWilds(current),
    event: Object.freeze({ god: 'SLIFER', order: 2, sliferTargets: Object.freeze(targets) }),
  });
}

function positionKey(position: GridPosition): string {
  return `${position.reel}:${position.row}`;
}

function withTemporaryWildCells(
  grid: Grid,
  cells: readonly GridPosition[],
  blocked: GridPosition | null,
  transformScatters: boolean,
): Grid {
  const selected = new Set(cells.map(positionKey));
  return Object.freeze(grid.map((column, columnIndex) => Object.freeze(column.map((symbol, row) => {
    if (blocked && blocked.reel === columnIndex && blocked.row === row) return 'POG';
    if (!selected.has(`${columnIndex}:${row}`) || (symbol === 'SCATTER' && !transformScatters)) return symbol;
    return 'WILD';
  })) as ReelColumn)) as Grid;
}

function compareFootprints(left: readonly GridPosition[], right: readonly GridPosition[]): number {
  const leftKey = left.map(positionKey).join('|');
  const rightKey = right.map(positionKey).join('|');
  return leftKey.localeCompare(rightKey);
}

/**
 * Builds every contiguous footprint for the requested permanent Obelisk level.
 * Level one starts at one complete reel; every later activation adds exactly
 * one adjacent cell, so levels 4/7/10/13 complete another neighboring reel.
 */
export function createObeliskFootprints(level: number): readonly (readonly GridPosition[])[] {
  if (!Number.isSafeInteger(level) || level < 1) throw new RangeError('Obelisk-Stufe ist ungültig.');
  const targetCount = Math.min(15, level + 2);
  const fullReels = Math.floor(targetCount / 3);
  const remainder = targetCount % 3;
  if (fullReels === 5) {
    return Object.freeze([Object.freeze(Array.from({ length: 15 }, (_, index) => Object.freeze({
      reel: Math.floor(index / 3) as ReelIndex,
      row: (index % 3) as RowIndex,
    })))]);
  }
  const footprints: GridPosition[][] = [];
  for (let startReel = 0; startReel <= 5 - fullReels; startReel += 1) {
    const base: GridPosition[] = [];
    for (let reel = startReel; reel < startReel + fullReels; reel += 1) {
      for (let row = 0; row < 3; row += 1) base.push({ reel: reel as ReelIndex, row: row as RowIndex });
    }
    if (remainder === 0) {
      footprints.push(base);
      continue;
    }
    const adjacentReels = [startReel - 1, startReel + fullReels]
      .filter((reel) => reel >= 0 && reel < 5);
    for (const adjacentReel of adjacentReels) {
      for (let firstRow = 0; firstRow <= 3 - remainder; firstRow += 1) {
        footprints.push([...base, ...Array.from({ length: remainder }, (_, offset) => ({
          reel: adjacentReel as ReelIndex,
          row: (firstRow + offset) as RowIndex,
        }))]);
      }
    }
  }
  const unique = new Map<string, readonly GridPosition[]>();
  for (const footprint of footprints) {
    const sorted = Object.freeze([...footprint].sort((left, right) => left.reel - right.reel || left.row - right.row));
    unique.set(sorted.map(positionKey).join('|'), sorted);
  }
  return Object.freeze([...unique.values()].sort(compareFootprints));
}

export function applyObelisk(
  grid: Grid,
  stops: ReelStops,
  betCents: MoneyCents,
  sticky: readonly WildMultiplierPosition[],
  blocked: GridPosition | null,
  level = 1,
  transformScatters = false,
): Readonly<{ spin: SpinResult; event: EgyptianGodEvent; evaluationGrid: Grid }> {
  const normalized = normalizeStickyWilds(sticky);
  const fullGrid = normalized.length === 15;
  const candidates: Array<{ summary: ObeliskCandidate; spin: SpinResult; grid: Grid }> = [];
  for (const footprint of createObeliskFootprints(level)) {
    const selectedKeys = new Set(footprint.map(positionKey));
    const evaluationGrid = fullGrid
      ? applyStickyWilds(grid, normalized)
      : withTemporaryWildCells(applyStickyWilds(grid, normalized), footprint, blocked, transformScatters);
    // Every selected Obelisk cell empowers an existing sticky wild by +1
    // for this evaluation only. Non-sticky cells become temporary 1x wilds.
    const multipliers = normalized.map((entry) => (
      selectedKeys.has(positionKey(entry)) ? { ...entry, multiplier: (entry.multiplier ?? 1) + 1 } : entry
    ));
    const spin = resolveSpin({ mode: 'free', betCents, grid, stops, evaluationGrid, wildMultipliers: multipliers });
    const primaryReel = footprint[0]!.reel;
    candidates.push({
      summary: Object.freeze({
        reel: primaryReel,
        cells: footprint,
        winCents: spin.totalWinCents,
        winningLines: spin.lineWins.length,
      }),
      spin,
      grid: evaluationGrid,
    });
  }
  candidates.sort((left, right) => (
    right.summary.winCents - left.summary.winCents
    || right.summary.winningLines - left.summary.winningLines
    || compareFootprints(left.summary.cells ?? [], right.summary.cells ?? [])
  ));
  const selected = candidates[0]!;
  const summaries = Object.freeze([...candidates]
    .sort((left, right) => compareFootprints(left.summary.cells ?? [], right.summary.cells ?? []))
    .map((candidate) => candidate.summary));
  return Object.freeze({
    spin: selected.spin,
    evaluationGrid: selected.grid,
    event: Object.freeze({
      god: 'OBELISK', order: 3,
      obeliskMode: fullGrid ? 'full-grid-multiplier' : 'wild-reel',
      obeliskCandidates: summaries,
      obeliskReel: selected.summary.reel,
      obeliskLevel: level,
      obeliskCells: selected.summary.cells,
      obeliskTransformsScatters: !fullGrid && transformScatters,
      gridBefore: applyStickyWilds(grid, normalized),
      gridAfter: selected.grid,
    }),
  });
}
